import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import Modal from "react-modal";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import axios from "axios";
import "./App.css";

interface CalculatorInputs {
  custodianWage: number;
  hoursPerWeek: number;
  robotCost: number;
}

interface RobotOption {
  name: string;
  price: number;
}

const ROBOT_OPTIONS: RobotOption[] = [
  { name: "Omnie", price: 52700 },
  { name: "Scrubber 50", price: 49700 },
  { name: "Scrubber 75", price: 90900 },
  { name: "Phantas", price: 26800 },
  { name: "Beetle", price: 27000 },
  { name: "Vacuum 40", price: 40200 },
];

interface RobotSpecs {
  chargingTime: string;
  runTime: string;
  cleanCapacity: {
    mode: string;
    capacity: string;
  }[];
}

const ROBOT_SPECS: Record<string, RobotSpecs> = {
  Beetle: {
    chargingTime: "2.5 hours",
    runTime: "3-5 hours",
    cleanCapacity: [{ mode: "Sweeping", capacity: "15,000-20,000 sq ft/hr" }],
  },
  Omnie: {
    chargingTime: "2 hours",
    runTime: "3 hours",
    cleanCapacity: [{ mode: "Scrubbing", capacity: "12,000-17,000 sq ft/hr" }],
  },
  Phantas: {
    chargingTime: "2 hours",
    runTime: "4 hours",
    cleanCapacity: [
      { mode: "Scrubbing", capacity: "3,300-7,500 sq ft/hr" },
      { mode: "Vacuuming", capacity: "12,700 sq ft/hr" },
    ],
  },
  "Vacuum 40": {
    chargingTime: "2.5 hours",
    runTime: "3 hours",
    cleanCapacity: [{ mode: "Vacuuming", capacity: "4,300-8,600 sq ft/hr" }],
  },
  "Scrubber 50": {
    chargingTime: "2 hours",
    runTime: "3 hours",
    cleanCapacity: [{ mode: "Scrubbing", capacity: "8,000-12,000 sq ft/hr" }],
  },
  "Scrubber 75": {
    chargingTime: "2 hours",
    runTime: "4-6 hours",
    cleanCapacity: [{ mode: "Scrubbing", capacity: "7,500-15,000 sq ft/hr" }],
  },
};

interface RobotImageConfig {
  folder: string;
  frameCount: number;
  filename: string;
  isTurnaround: boolean;
}

const ROBOT_IMAGE_CONFIG: Record<string, RobotImageConfig> = {
  Beetle: {
    folder: "Beetle",
    frameCount: 1,
    filename: "Beetle.png",
    isTurnaround: false,
  },
  Omnie: {
    folder: "Onmie",
    frameCount: 1,
    filename: "Onmie 2.png",
    isTurnaround: false,
  },
  Phantas: {
    folder: "Phantas",
    frameCount: 59,
    filename: "{frame}.png",
    isTurnaround: true,
  },
  "Scrubber 50": {
    folder: "Scrubber 50",
    frameCount: 58,
    filename: "{frame}.png",
    isTurnaround: true,
  },
  "Scrubber 75": {
    folder: "Scrubber 75",
    frameCount: 59,
    filename: "{frame}.png",
    isTurnaround: true,
  },
  "Vacuum 40": {
    folder: "Vacuum 40",
    frameCount: 58,
    filename: "{frame}.png",
    isTurnaround: true,
  },
};

interface CalculationResults {
  laborBurden: number;
  hoursPerYear: number;
  annualCost: number;
  custodianEfficiency: {
    sqftPerHour: number;
    costPerSqft: number;
  };
  robotEfficiency: {
    sqftPerHour: number;
    costPerSqft: number;
  };
  yearlySavings: Array<{
    year: number;
    savings: number;
    cumulativeSavings: number;
    roi: number;
  }>;
  paybackPeriod: number;
}

function App() {
  const [inputs, setInputs] = useState<CalculatorInputs>({
    custodianWage: 20.0,
    hoursPerWeek: 40,
    robotCost: 49700.0,
  });

  // String versions of inputs for display in form fields
  const [inputStrings, setInputStrings] = useState({
    custodianWage: "20.00",
    hoursPerWeek: "40",
    robotCost: "49700.00",
  });

  // Selected robot (default to Scrubber 50)
  const [selectedRobot, setSelectedRobot] = useState<string>("Scrubber 50");

  const [results, setResults] = useState<CalculationResults>({
    laborBurden: 0,
    hoursPerYear: 0,
    annualCost: 0,
    custodianEfficiency: { sqftPerHour: 0, costPerSqft: 0 },
    robotEfficiency: { sqftPerHour: 0, costPerSqft: 0 },
    yearlySavings: [],
    paybackPeriod: 0,
  });

  // 3D Robot View state
  const [currentFrame, setCurrentFrame] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [preloadedImages, setPreloadedImages] = useState<HTMLImageElement[]>(
    []
  );

  // PDF Export state
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false);
  const [emailAddress, setEmailAddress] = useState("");
  const [senderName, setSenderName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");

  const handleInputChange = (field: keyof CalculatorInputs, value: string) => {
    // Update the string version immediately for responsive UI
    setInputStrings((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Convert to number for calculations
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0) {
      setInputs((prev) => ({
        ...prev,
        [field]: numValue,
      }));
    } else if (value === "") {
      // Allow empty string, set to 0 for calculations
      setInputs((prev) => ({
        ...prev,
        [field]: 0,
      }));
    }
  };

  const handleRobotChange = (robotName: string) => {
    setSelectedRobot(robotName);
    const robot = ROBOT_OPTIONS.find((r) => r.name === robotName);
    if (robot) {
      const priceStr = robot.price.toFixed(2);
      setInputStrings((prev) => ({
        ...prev,
        robotCost: priceStr,
      }));
      setInputs((prev) => ({
        ...prev,
        robotCost: robot.price,
      }));
    }
  };

  // Calculate all results when inputs change
  useEffect(() => {
    const calculateResults = () => {
      // Fixed values
      const laborBurden = 0.3; // 30%
      const weeksPerYear = 50;
      const custodianSqftPerHour = 3500; // APPA 3
      const robotSqftPerHour = 9000;
      const robotLifespan = 5; // years

      // Basic calculations
      const hoursPerYear = inputs.hoursPerWeek * weeksPerYear;
      const annualCost =
        inputs.custodianWage * hoursPerYear * (1 + laborBurden);

      // Efficiency calculations
      const custodianCostPerSqft =
        annualCost / (custodianSqftPerHour * hoursPerYear);
      const robotAnnualCost = inputs.robotCost / robotLifespan;
      const robotCostPerSqft =
        robotAnnualCost / (robotSqftPerHour * hoursPerYear);

      // 5-year savings and ROI calculations
      const yearlySavings = [];
      let cumulativeSavings = 0;

      for (let year = 1; year <= 5; year++) {
        // Annual savings calculation:
        // Year 1: Annual labor cost - Robot cost
        // Following years: Just the annual labor cost
        const annualSavings =
          year === 1 ? annualCost - inputs.robotCost : annualCost;

        // Cumulative savings = sum of all annual savings values until current year
        cumulativeSavings += annualSavings;

        // ROI = cumulative savings / robot cost
        const roi = (cumulativeSavings / inputs.robotCost) * 100;

        yearlySavings.push({
          year,
          savings: annualSavings,
          cumulativeSavings,
          roi,
        });
      }

      // Payback period calculation
      const paybackPeriod = inputs.robotCost / annualCost;

      setResults({
        laborBurden: laborBurden * 100,
        hoursPerYear,
        annualCost,
        custodianEfficiency: {
          sqftPerHour: custodianSqftPerHour,
          costPerSqft: custodianCostPerSqft,
        },
        robotEfficiency: {
          sqftPerHour: robotSqftPerHour,
          costPerSqft: robotCostPerSqft,
        },
        yearlySavings,
        paybackPeriod,
      });
    };

    calculateResults();
  }, [inputs]);

  // Preload robot sequence images
  useEffect(() => {
    const preloadImages = async () => {
      const images: HTMLImageElement[] = [];
      const config = ROBOT_IMAGE_CONFIG[selectedRobot];

      if (!config) return;

      if (config.isTurnaround) {
        // Load all frames for turnaround
        for (let i = 1; i <= config.frameCount; i++) {
          const img = new Image();
          img.src = require(`./assets/robot-sequence/${config.folder}/${i}.png`);
          images.push(img);
        }
      } else {
        // Load single static image
        const img = new Image();
        img.src = require(`./assets/robot-sequence/${config.folder}/${config.filename}`);
        images.push(img);
      }

      // Wait for all images to load
      await Promise.all(
        images.map(
          (img) =>
            new Promise((resolve) => {
              img.onload = resolve;
              img.onerror = resolve; // Continue even if some images fail
            })
        )
      );

      setPreloadedImages(images);
    };

    preloadImages();
  }, [selectedRobot]);

  // Reset frame when robot changes
  useEffect(() => {
    setCurrentFrame(1);
  }, [selectedRobot]);

  // Handle slider change for 3D robot view
  const handleSliderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value);
    setCurrentFrame(value);
  };

  // Open modal
  const openModal = () => {
    // Ensure a robot is selected (default to Scrubber 50)
    if (!selectedRobot) {
      setSelectedRobot("Scrubber 50");
    }
    // Prevent body scroll when modal opens
    document.body.style.overflow = "hidden";
    setIsModalOpen(true);
  };

  // Close modal
  const closeModal = () => {
    // Restore body scroll when modal closes
    document.body.style.overflow = "unset";
    setIsModalOpen(false);
  };

  // Open email dialog
  const openEmailDialog = () => {
    setIsEmailDialogOpen(true);
    document.body.style.overflow = "hidden";
  };

  // Close email dialog
  const closeEmailDialog = () => {
    setIsEmailDialogOpen(false);
    document.body.style.overflow = "unset";
    setEmailAddress(""); // Clear email on close
    setSenderName(""); // Clear name on close
    setPhoneNumber(""); // Clear phone on close
  };

  // Export and send PDF via email
  const exportAndSendPDF = async () => {
    if (isExportingPDF || !emailAddress.trim() || !senderName.trim() || !phoneNumber.trim()) return;

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailAddress.trim())) {
      alert("Please enter a valid email address");
      return;
    }

    // Validate name
    if (senderName.trim().length < 2) {
      alert("Please enter a valid name");
      return;
    }

    // Validate phone number
    if (phoneNumber.trim().length < 10) {
      alert("Please enter a valid phone number");
      return;
    }

    setIsExportingPDF(true);
    closeEmailDialog();

    try {
      // Capture the entire App element
      const element = document.querySelector(".App") as HTMLElement;

      if (!element) {
        throw new Error("Could not find App element");
      }

      // Generate canvas from the element
      const canvas = await html2canvas(element, {
        scale: 2, // Higher quality
        useCORS: true, // Allow cross-origin images
        logging: false,
        backgroundColor: "#ffffff",
        windowWidth: element.scrollWidth,
        windowHeight: element.scrollHeight,
      });

      // Convert canvas to PDF
      const imgData = canvas.toDataURL("image/jpeg");
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const imgWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      // Add first page
      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // Add additional pages if content is longer than one page
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Get PDF as blob
      const pdfBlob = pdf.output("blob");

      // Create FormData for API request (multipart/form-data)
      const formData = new FormData();
      const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
      const filename = `roi-calculator-${timestamp}.pdf`;

      // Prepare email content with calculation summary
      const emailSubject = "CleanBotics ROI Calculator Results";
      const emailBody = `
Dear Valued Customer,

Thank you for using the CleanBotics ROI Calculator. Please find attached your personalized ROI analysis.

===== CONTACT INFORMATION =====

Name: ${senderName.trim()}
Phone Number: ${phoneNumber.trim()}
Email: ${emailAddress.trim()}

===== CALCULATION SUMMARY =====

Robot Selected: ${selectedRobot}
Robot Cost: $${inputs.robotCost.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}

Custodian Wage: $${inputs.custodianWage.toFixed(2)}/hour
Hours per Week: ${inputs.hoursPerWeek}
Annual Labor Cost: $${results.annualCost.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}

Payback Period: ${results.paybackPeriod.toFixed(2)} Years

5-Year ROI: ${results.yearlySavings[4]?.roi.toFixed(1)}%
5-Year Cumulative Savings: $${results.yearlySavings[4]?.cumulativeSavings.toLocaleString(
        undefined,
        { minimumFractionDigits: 2, maximumFractionDigits: 2 }
      )}

===== CONTACT US =====

We'd love to discuss how CleanBotics can transform your facility operations.

Website: https://www.cleanbotics.net
Email: sales@cleanbotics.net

Best regards,
The CleanBotics Team

---
This email was generated by the CleanBotics ROI Calculator.
© ${new Date().getFullYear()} CleanBotics. All rights reserved.
`.trim();

      // Append data to FormData
      formData.append("toEmail", emailAddress.trim());
      formData.append("subject", emailSubject);
      formData.append("body", emailBody);
      formData.append("fromName", "CleanBotics ROI Calculator");
      formData.append("attachment", pdfBlob, filename);
      formData.append("name", senderName)
      formData.append("phone", phoneNumber);

      // Debug logging - verify FormData contents
      console.log("========== EMAIL REQUEST DEBUG ==========");
      console.log("Sending PDF email to:", emailAddress.trim());
      console.log("Email subject:", emailSubject);
      console.log("Email body length:", emailBody.length, "characters");
      console.log("PDF filename:", filename);
      console.log("PDF blob size:", pdfBlob.size, "bytes");
      console.log("PDF blob type:", pdfBlob.type);

      // Log all FormData entries
      const formDataEntries: Record<string, any> = {};
      formData.forEach((value, key) => {
        if (value instanceof Blob) {
          formDataEntries[
            key
          ] = `Blob (${value.size} bytes, type: ${value.type})`;
        } else {
          formDataEntries[key] =
            typeof value === "string" && value.length > 100
              ? `${value.substring(0, 100)}...`
              : value;
        }
      });
      console.log("FormData entries:", formDataEntries);
      console.log("========================================\n");

      // Send to API endpoint using axios with explicit config
      // Note: Do NOT set Content-Type manually for FormData
      // Axios will automatically set it with the correct boundary
      const response = await axios({
        method: "post",
        // url: "https://localhost:7093/email",
        url: "https://api.cleanbotics.dmburr.spuderp.com/email",
        headers: { "Content-Type": "multipart/form-data" },
        data: formData,
        onUploadProgress: (progressEvent) => {
          const percentCompleted = progressEvent.total
            ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
            : 0;
          console.log(`Upload Progress: ${percentCompleted}%`);
        },
      });

      // Log successful response
      console.log("API Response:", response.data);
      console.log("Response Status:", response.status);
      console.log("Response Headers:", response.headers);

      // Success feedback
      alert(`Success! The ROI Calculator PDF has been sent to ${emailAddress}. You will receive it shortly.`);
      console.log("PDF sent successfully");

      // Clear all fields after success
      setEmailAddress("");
      setSenderName("");
      setPhoneNumber("");

    } catch (error) {
      console.error("Error exporting/sending PDF:", error);

      // Handle axios errors with more detail
      if (axios.isAxiosError(error)) {
        const errorMessage =
          error.response?.data?.message ||
          error.response?.data ||
          error.message;
        console.error("API Error Details:", {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          headers: error.response?.headers,
        });
        alert(`Failed to send PDF: ${errorMessage}`);
      } else {
        alert(
          `Failed to send PDF: ${
            error instanceof Error ? error.message : "Unknown error"
          }`
        );
      }
    } finally {
      setIsExportingPDF(false);
    }
  };

  return (
    <div className="App">
      {/* Header Section */}
      <motion.header
        className="app-header"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <div className="header-content">
          <motion.div
            className="logo-section"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
          >
            <motion.div
              className="logo-placeholder"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
            >
              <img
                className="cleanbotics-logo"
                src="/logo-small.png"
                alt="CleanBotics Logo"
              />
            </motion.div>
            <div className="logo-text">
              <h1>
                Cle<span style={{ color: "#0096FF" }}>A</span>nBot
                <span style={{ color: "#0096FF" }}>I</span>cs ROI Calculator
              </h1>
              <p>Robotic cleaning solutions for modern facilities</p>
            </div>
          </motion.div>
        </div>
      </motion.header>

      {/* Main Content */}
      <main className="app-main">
        <div className="dashboard-container">
          {/* Row 1: Configuration and Cost Analysis */}
          <motion.div
            className="dashboard-row row-1"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4, ease: "easeOut" }}
          >
            {/* Configuration Section */}
            <motion.section
              className="input-section config-card"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.5, ease: "easeOut" }}
              whileHover={{ y: -2 }}
            >
              <div className="card-header">
                <h3>⚙️ Configuration</h3>
              </div>
              <div className="input-grid">
                <div className="input-group">
                  <label htmlFor="custodianWage">
                    Custodian Wage (per hour)
                  </label>
                  <div className="input-wrapper">
                    <span className="currency-symbol">$</span>
                    <input
                      id="custodianWage"
                      type="number"
                      step="0.01"
                      value={inputStrings.custodianWage}
                      onChange={(e) =>
                        handleInputChange("custodianWage", e.target.value)
                      }
                      className="form-input"
                    />
                  </div>
                </div>

                <div className="input-group">
                  <label htmlFor="hoursPerWeek">Hours per Week</label>
                  <div className="input-wrapper">
                    <input
                      id="hoursPerWeek"
                      type="number"
                      value={inputStrings.hoursPerWeek}
                      onChange={(e) =>
                        handleInputChange("hoursPerWeek", e.target.value)
                      }
                      className="form-input"
                    />
                    <span className="unit-symbol">hrs</span>
                  </div>
                </div>

                <div className="input-group">
                  <label htmlFor="robotCost">Robot + Workstation Cost</label>
                  <div className="input-wrapper">
                    <select
                      id="robotCost"
                      value={selectedRobot}
                      onChange={(e) => handleRobotChange(e.target.value)}
                      className="form-input robot-select"
                    >
                      {ROBOT_OPTIONS.map((robot) => (
                        <option key={robot.name} value={robot.name}>
                          {robot.name} - ${robot.price.toLocaleString()}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </motion.section>

            {/* Cost Analysis */}
            <motion.section
              className="result-card cost-analysis-card"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.6, ease: "easeOut" }}
              whileHover={{ y: -2 }}
            >
              <div className="card-header">
                <h3>💰 Cost Analysis</h3>
              </div>
              <div className="card-content">
                <div className="cost-analysis-list">
                  <div className="cost-item">
                    <span className="cost-label">Wage (per hour)</span>
                    <span className="cost-value">
                      ${inputs.custodianWage.toFixed(2)}
                    </span>
                  </div>
                  <div className="cost-item">
                    <span className="cost-label">Labor Burden (average)</span>
                    <span className="cost-value">
                      {results.laborBurden.toFixed(1)}%
                    </span>
                  </div>
                  <div className="cost-item">
                    <span className="cost-label">Hours per Week</span>
                    <span className="cost-value">
                      {inputs.hoursPerWeek.toLocaleString()}
                    </span>
                  </div>
                  <div className="cost-item">
                    <span className="cost-label">Hours per Year</span>
                    <span className="cost-value">
                      {results.hoursPerYear.toLocaleString()}
                    </span>
                  </div>
                  <div className="cost-item highlight-item">
                    <span className="cost-label">Annual Labor Cost</span>
                    <span className="cost-value currency">
                      $
                      {results.annualCost.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  </div>
                </div>
              </div>
            </motion.section>
          </motion.div>

          {/* Row 2: Efficiency Comparison and Payback Period */}
          <motion.div
            className="dashboard-row row-2"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7, ease: "easeOut" }}
          >
            {/* Efficiency Comparison */}
            <motion.section
              className="result-card efficiency-comparison-card tall-card"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.8, ease: "easeOut" }}
              whileHover={{ y: -2 }}
            >
              <div className="card-header">
                <h3>⚡ Efficiency Comparison</h3>
              </div>
              <div className="card-content">
                <div className="efficiency-table-container">
                  <div className="efficiency-table">
                    {/* Single Gradient Header Row */}
                    <div className="efficiency-single-header">
                      <div className="header-cell">TYPE</div>
                      <div className="header-cell">SQ FT/HR</div>
                      <div className="header-cell">COST/SQ FT</div>
                    </div>

                    {/* Custodian Row */}
                    <div className="efficiency-data-row">
                      <div className="efficiency-label">Custodian</div>
                      <div className="efficiency-value sqft-value">
                        {results.custodianEfficiency.sqftPerHour.toLocaleString()}
                      </div>
                      <div className="efficiency-value cost-value">
                        ${results.custodianEfficiency.costPerSqft.toFixed(4)}
                      </div>
                    </div>

                    {/* Robot Row */}
                    <div className="efficiency-data-row">
                      <div className="efficiency-label">Robot</div>
                      <div className="efficiency-value sqft-value">
                        {results.robotEfficiency.sqftPerHour.toLocaleString()}
                      </div>
                      <div className="efficiency-value cost-value">
                        ${results.robotEfficiency.costPerSqft.toFixed(4)}
                      </div>
                    </div>

                    {/* Improvement Row */}
                    <div className="efficiency-improvement-row">
                      <div className="improvement-label">Improvement</div>
                      <div className="improvement-value sqft-improvement">
                        +
                        {(
                          (results.robotEfficiency.sqftPerHour /
                            results.custodianEfficiency.sqftPerHour -
                            1) *
                          100
                        ).toFixed(0)}
                        %
                      </div>
                      <div className="improvement-value cost-improvement">
                        -
                        {(
                          (1 -
                            results.robotEfficiency.costPerSqft /
                              results.custodianEfficiency.costPerSqft) *
                          100
                        ).toFixed(0)}
                        %
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.section>

            {/* Payback Period */}
            <motion.section
              className="result-card payback-card"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.9, ease: "easeOut" }}
              whileHover={{ y: -2 }}
            >
              <div className="card-header">
                <h3>Payback Period</h3>
                <div className="card-icon">⏱️</div>
              </div>
              <div className="card-content">
                <div className="payback-display">
                <svg width="200" height="50">
                  <defs>
                    <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#94C335" stopOpacity={1} />
                      <stop offset="100%" stopColor="#1B947F" stopOpacity={1} />
                    </linearGradient>
                  </defs>
                  <text x="0" y="40" font-size="40" font-weight="bold" fill="url(#grad)">
                    {results.paybackPeriod.toFixed(2)} Years
                  </text>
                </svg>

                  <div className="payback-description">
                    Time to recover initial robot investment
                  </div>
                  <table className="professional-table compact">
                    <tbody>
                      <tr>
                        <td className="table-label">
                          Robot + Workstation Cost:
                        </td>
                        <td className="table-value currency">
                          $
                          {inputs.robotCost.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </td>
                      </tr>
                      <tr>
                        <td className="table-label">Annual Labor Cost:</td>
                        <td className="table-value currency">
                          $
                          {results.annualCost.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </motion.section>
          </motion.div>

          {/* Row 3: Savings & ROI */}
          <motion.div
            className="dashboard-row row-3"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.0, ease: "easeOut" }}
          >
            {/* Savings & ROI Table */}
            <motion.section
              className="result-card savings-roi-card full-width-card"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 1.1, ease: "easeOut" }}
              whileHover={{ y: -2 }}
            >
              <div className="card-header">
                <h3>Savings & ROI (5 Years)</h3>
                <div className="card-icon">📊</div>
              </div>
              <div className="card-content">
                <table className="professional-table">
                  <thead>
                    <tr>
                      <th className="table-header">YEAR</th>
                      <th className="table-header">ANNUAL SAVINGS</th>
                      <th className="table-header">CUMULATIVE SAVINGS</th>
                      <th className="table-header">ROI</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.yearlySavings.map((year) => (
                      <tr
                        key={year.year}
                        className={year.year === 1 ? "year-one-row" : ""}
                      >
                        <td className="table-label">Year {year.year}</td>
                        <td className="table-value currency">
                          $
                          {year.savings.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </td>
                        <td className="table-value currency">
                          $
                          {year.cumulativeSavings.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </td>
                        <td
                          className={`table-value ${
                            year.roi >= 0 ? "positive" : "negative"
                          }`}
                        >
                          {year.roi.toFixed(1)}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.section>
          </motion.div>
        </div>
      </main>

      {/* 3D Robot Modal */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        className="robot-modal"
        overlayClassName="robot-modal-overlay"
        ariaHideApp={false}
      >
        <div className="modal-content">
          <div className="modal-header">
            <h2>{selectedRobot}</h2>
            <button
              className="close-button"
              onClick={closeModal}
              aria-label="Close modal"
            >
              ×
            </button>
          </div>

          <div className="modal-body">
            <div className="robot-image-container">
              {preloadedImages.length > 0 &&
                ROBOT_IMAGE_CONFIG[selectedRobot] &&
                (ROBOT_IMAGE_CONFIG[selectedRobot].isTurnaround ? (
                  <img
                    src={require(`./assets/robot-sequence/${ROBOT_IMAGE_CONFIG[selectedRobot].folder}/${currentFrame}.png`)}
                    alt={`${selectedRobot} - Frame ${currentFrame}`}
                    className="modal-robot-image"
                  />
                ) : (
                  <img
                    src={require(`./assets/robot-sequence/${ROBOT_IMAGE_CONFIG[selectedRobot].folder}/${ROBOT_IMAGE_CONFIG[selectedRobot].filename}`)}
                    alt={selectedRobot}
                    className="modal-robot-image"
                  />
                ))}
            </div>

            {ROBOT_IMAGE_CONFIG[selectedRobot]?.isTurnaround && (
              <div className="slider-container">
                <label htmlFor="robot-slider" className="slider-label">
                  👉 Pull the Slider to Rotate
                </label>
                <input
                  id="robot-slider"
                  type="range"
                  min="1"
                  max={ROBOT_IMAGE_CONFIG[selectedRobot].frameCount}
                  step="1"
                  value={currentFrame}
                  onChange={handleSliderChange}
                  className="robot-slider"
                />
              </div>
            )}

            {/* Robot Specifications */}
            {ROBOT_SPECS[selectedRobot] && (
              <div className="robot-specs-section">
                <h3 className="specs-title">Specifications</h3>
                <div className="specs-grid">
                  <div className="spec-item">
                    <div className="spec-icon">⚡</div>
                    <div className="spec-content">
                      <div className="spec-label">Charging Time</div>
                      <div className="spec-value">
                        {ROBOT_SPECS[selectedRobot].chargingTime}
                      </div>
                    </div>
                  </div>

                  <div className="spec-item">
                    <div className="spec-icon">🔋</div>
                    <div className="spec-content">
                      <div className="spec-label">Run Time</div>
                      <div className="spec-value">
                        {ROBOT_SPECS[selectedRobot].runTime}
                      </div>
                    </div>
                  </div>

                  <div className="spec-item full-width">
                    <div className="spec-icon">🧹</div>
                    <div className="spec-content">
                      <div className="spec-label">Cleaning Capacity</div>
                      <div className="capacity-list">
                        {ROBOT_SPECS[selectedRobot].cleanCapacity.map(
                          (capacity, index) => (
                            <div key={index} className="capacity-item">
                              <span className="capacity-mode">
                                {capacity.mode}:
                              </span>
                              <span className="capacity-value">
                                {capacity.capacity}
                              </span>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </Modal>

      {/* Email Dialog Modal */}
      <Modal
        isOpen={isEmailDialogOpen}
        onRequestClose={closeEmailDialog}
        className="email-dialog-modal"
        overlayClassName="robot-modal-overlay"
        ariaHideApp={false}
      >
        <div className="email-dialog-content">
          <div className="email-dialog-header">
            <h2>Send PDF via Email</h2>
            <button
              className="close-button"
              onClick={closeEmailDialog}
              aria-label="Close dialog"
            >
              ×
            </button>
          </div>

          <div className="email-dialog-body">
            <p className="email-dialog-description">
              Enter your contact information to receive the ROI calculator PDF.
            </p>

            <div className="email-input-group">
              <label htmlFor="name-input">Name *</label>
              <input
                id="name-input"
                type="text"
                value={senderName}
                onChange={(e) => setSenderName(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    exportAndSendPDF();
                  }
                }}
                placeholder="John Doe"
                className="email-input"
                autoFocus
                required
              />
            </div>
            
            <div className="email-input-group">
              <label htmlFor="phone-input">Phone Number *</label>
              <input
                id="phone-input"
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    exportAndSendPDF();
                  }
                }}
                placeholder="(555) 123-4567"
                className="email-input"
                required
              />
            </div>
            
            <div className="email-input-group">
              <label htmlFor="email-input">Email Address *</label>
              <input
                id="email-input"
                type="email"
                value={emailAddress}
                onChange={(e) => setEmailAddress(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    exportAndSendPDF();
                  }
                }}
                placeholder="example@company.com"
                className="email-input"
                required
              />
            </div>

            <div className="email-dialog-actions">
              <button
                className="email-cancel-button"
                onClick={closeEmailDialog}
              >
                Cancel
              </button>
              <button
                className="email-send-button"
                onClick={exportAndSendPDF}
                disabled={!emailAddress.trim() || !senderName.trim() || !phoneNumber.trim()}
              >
                <span className="button-icon">📧</span>
                Send PDF
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Floating Action Button - 3D View */}
      <motion.button
        className="fab-robot-button"
        onClick={openModal}
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, delay: 1.5, ease: "easeOut" }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
      >
        <span className="fab-icon">🤖</span>
        <span className="fab-text">3D View</span>
      </motion.button>

      {/* Floating Action Button - Send Email */}
      <motion.button
        className="fab-export-button"
        onClick={openEmailDialog}
        disabled={isExportingPDF}
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, delay: 1.6, ease: "easeOut" }}
        whileHover={{ scale: isExportingPDF ? 1 : 1.1 }}
        whileTap={{ scale: isExportingPDF ? 1 : 0.95 }}
      >
        <span className="fab-icon">{isExportingPDF ? "⏳" : "📧"}</span>
        <span className="fab-text">
          {isExportingPDF ? "Sending..." : "Send Email"}
        </span>
      </motion.button>

      {/* Footer */}
      <motion.footer
        className="app-footer"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 1.2, ease: "easeOut" }}
      >
        <div className="footer-content">
          <div className="footer-links">
            <span className="footer-link-text">
              Website:{" "}
              <a
                href="https://www.cleanbotics.net"
                target="_blank"
                rel="noopener noreferrer"
                className="footer-link"
              >
                https://www.cleanbotics.net
              </a>
            </span>
            <span className="footer-link-text">
              Contact Us:{" "}
              <a
                href="mailto:sales@cleanbotics.net?subject=ROI%20Calculator%20Inquiry"
                className="footer-link"
              >
                sales@cleanbotics.net
              </a>
            </span>
          </div>
          <div className="footer-copyright">
            © {new Date().getFullYear()} CleanBotics. All rights reserved.
          </div>
        </div>
      </motion.footer>
    </div>
  );
}

export default App;
